import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu as MenuIcon, X, ChevronDown, LogOut, Package, Settings } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import Avatar from "../common/Avatar";

const navLinkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors ${
    isActive ? "text-neutral-900" : "text-neutral-500 hover:text-neutral-900"
  }`;

export default function Navbar() {
  const { isAuthenticated, user, logout } = useAuth();
  const { itemCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Close the desktop dropdown on an outside click — menus left open
  // after a click elsewhere read as broken, not just unpolished.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    setMobileOpen(false);
    await logout();
    navigate("/");
  };

  const links = (
    <>
      <NavLink to="/" end className={navLinkClass} onClick={() => setMobileOpen(false)}>
        Home
      </NavLink>
      <NavLink to="/shop" className={navLinkClass} onClick={() => setMobileOpen(false)}>
        Shop
      </NavLink>
      <NavLink to="/categories" className={navLinkClass} onClick={() => setMobileOpen(false)}>
        Categories
      </NavLink>
    </>
  );

  // Customer-only menu — deliberately just the 3 items the task asked for,
  // all pointing at pages that already exist (no new routes). "My Profile"
  // and "My Account" both resolve to /account, since profile editing
  // already lives inside Account.jsx rather than a separate page.
  const customerMenuItems = [
    { label: "My Account", to: "/account", icon: User },
    { label: "My Orders", to: "/orders", icon: Package },
    { label: "My Profile", to: "/account", icon: Settings },
  ];

  return (
    <header className="sticky top-0 z-20 border-b border-neutral-200 bg-white/90 backdrop-blur">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="text-lg font-semibold text-neutral-900 shrink-0">
          Shop
        </Link>

        <nav className="hidden md:flex items-center gap-6">{links}</nav>

        <div className="flex items-center gap-4 shrink-0">
          {isAuthenticated ? (
            <div className="relative hidden sm:block" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-medium text-neutral-700 hover:text-neutral-900"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <Avatar name={user?.name} photo={user?.photo} size="h-7 w-7" textSize="text-[10px]" />
                <span className="max-w-[9rem] truncate">{user?.name || "Account"}</span>
                <ChevronDown size={14} className={menuOpen ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-48 rounded-xl border border-neutral-200 bg-white shadow-lg py-1.5 overflow-hidden"
                >
                  {customerMenuItems.map(({ label, to, icon: Icon }) => (
                    <Link
                      key={label}
                      to={to}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3.5 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      <Icon size={15} className="text-neutral-400" />
                      {label}
                    </Link>
                  ))}
                  <div className="border-t border-neutral-100 my-1" />
                  <button
                    onClick={handleLogout}
                    role="menuitem"
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut size={15} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
            >
              <User size={18} />
              Login
            </Link>
          )}

          <Link
            to="/cart"
            className="relative flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
          >
            <ShoppingCart size={18} />
            Cart
            {itemCount > 0 && (
              <span className="absolute -top-2 -right-3 bg-neutral-900 text-white text-[10px] leading-none rounded-full w-4 h-4 flex items-center justify-center">
                {itemCount > 9 ? "9+" : itemCount}
              </span>
            )}
          </Link>

          <button
            className="md:hidden p-1.5 text-neutral-600"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <MenuIcon size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-neutral-200 px-4 py-3 flex flex-col gap-3 bg-white">
          {links}

          {isAuthenticated ? (
            <>
              <div className="border-t border-neutral-100 pt-3 mt-1">
                <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-2">
                  {user?.name || "Account"}
                </p>
                <div className="flex flex-col gap-2.5">
                  {customerMenuItems.map(({ label, to, icon: Icon }) => (
                    <Link
                      key={label}
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2.5 text-sm font-medium text-neutral-600"
                    >
                      <Icon size={16} className="text-neutral-400" />
                      {label}
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2.5 text-sm font-medium text-red-600 text-left"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </div>
            </>
          ) : (
            <button
              onClick={() => {
                setMobileOpen(false);
                navigate("/login");
              }}
              className="text-sm font-medium text-neutral-500 text-left"
            >
              Login
            </button>
          )}
        </div>
      )}
    </header>
  );
}
